// AuthModal.tsx
// Moderne login/register modal met purchase intent support
// Na login: als er een pendingPurchase is, redirect naar Stripe checkout
// ✅ NIEUW: Wachtwoord vergeten functionaliteit
// ✅ FIX: Verbeterde auth state synchronization - wacht op context update

import React, { useState, Fragment, useEffect, useCallback } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { trackSignUp, trackLogin } from '../services/analytics';
import { auth } from '../config/firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

// Simple X Icon component
const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Arrow Left Icon for back button
const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

// Mail Icon for success state
const MailIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

interface PendingPurchase {
  productId: string;
  productName: string;
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'signin' | 'signup';
  pendingPurchase?: PendingPurchase | null;
}

// View types
type ModalView = 'auth' | 'forgot-password';

const AuthModal: React.FC<AuthModalProps> = ({ 
  isOpen, 
  onClose, 
  defaultTab = 'signin',
  pendingPurchase = null 
}) => {
  const { login, register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  
  // View state - auth (login/register) of forgot-password
  const [currentView, setCurrentView] = useState<ModalView>('auth');
  
  // Tab state (0 = Sign In, 1 = Sign Up)
  const [selectedTab, setSelectedTab] = useState(defaultTab === 'signin' ? 0 : 1);
  
  // Sign In state
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  
  // Sign Up state
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState('');
  
  // Forgot Password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  // UI state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // ✅ NIEUW: Track of we wachten op navigatie na auth success
  const [pendingNavigation, setPendingNavigation] = useState(false);

  // Update tab when defaultTab changes
  useEffect(() => {
    setSelectedTab(defaultTab === 'signin' ? 0 : 1);
  }, [defaultTab]);

  // Reset view when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentView('auth');
      setResetEmailSent(false);
      setResetEmail('');
      setError('');
      setPendingNavigation(false);
    }
  }, [isOpen]);

  // Store pending purchase in localStorage when modal opens
  useEffect(() => {
    if (isOpen && pendingPurchase) {
      localStorage.setItem('pendingPurchase', JSON.stringify(pendingPurchase));
      console.log('💾 Stored pending purchase:', pendingPurchase);
    }
  }, [isOpen, pendingPurchase]);

  // ✅ FIX: Navigeer pas wanneer de user in de context is geüpdatet
  // Dit voorkomt de race condition waarbij ProtectedRoute de user nog niet ziet
  useEffect(() => {
    if (pendingNavigation && user) {
      console.log('✅ User detected in context, proceeding with navigation...');
      
      // Check for pending purchase
      const storedPurchase = localStorage.getItem('pendingPurchase');
      
      if (storedPurchase) {
        handlePendingPurchaseNavigation(storedPurchase);
      } else {
        // No pending purchase - navigate to dashboard
        completeNavigation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNavigation, user]);

  // Handle pending purchase checkout
  const handlePendingPurchaseNavigation = async (storedPurchase: string) => {
    try {
      const purchase = JSON.parse(storedPurchase) as PendingPurchase;
      console.log('🛒 Processing pending purchase:', purchase);
      
      // Clear the pending purchase
      localStorage.removeItem('pendingPurchase');
      
      // Create Stripe checkout session
      const token = await user!.getIdToken();
      
      const response = await fetch(`${BACKEND_URL}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ productId: purchase.productId }),
      });
      
      const data = await response.json();
      
      if (data.success && data.url) {
        console.log('💳 Redirecting to Stripe checkout...');
        resetModalState();
        onClose();
        window.location.href = data.url;
        return;
      } else {
        console.error('❌ Failed to create checkout:', data);
        // Fallback to dashboard
        completeNavigation();
      }
    } catch (err) {
      console.error('❌ Error processing pending purchase:', err);
      localStorage.removeItem('pendingPurchase');
      // Fallback to dashboard
      completeNavigation();
    }
  };

  // Complete navigation to dashboard
  const completeNavigation = useCallback(() => {
    console.log('🚀 Navigation complete - going to dashboard');
    resetModalState();
    
    // Navigate to dashboard
    navigate('/app/dashboard', { replace: true });
    
    // Close modal after navigation
    onClose();
  }, [navigate, onClose]);

  // Reset modal state
  const resetModalState = () => {
    setSignInEmail('');
    setSignInPassword('');
    setSignUpEmail('');
    setSignUpPassword('');
    setSignUpConfirmPassword('');
    setResetEmail('');
    setResetEmailSent(false);
    setError('');
    setLoading(false);
    setCurrentView('auth');
    setPendingNavigation(false);
  };

  const handleClose = () => {
    resetModalState();
    onClose();
  };

  // ✅ FIX: Handle successful authentication - set pending navigation flag
  // Instead of immediately navigating, we set a flag and let the useEffect
  // handle the navigation once the user is available in context
  const handleAuthSuccess = async (authUser: any) => {
    console.log('✅ Auth success, setting pending navigation...');
    
    // Keep loading state active while we wait for context update
    setLoading(true);
    
    // Set the pending navigation flag
    // The useEffect will pick this up once user is in context
    setPendingNavigation(true);
    
    // Safety timeout - if context doesn't update within 5 seconds, force navigate
    setTimeout(() => {
      if (pendingNavigation) {
        console.log('⚠️ Safety timeout - forcing navigation');
        completeNavigation();
      }
    }, 5000);
  };

  const handleGoogleLogin = async () => {
    // Voorkom dubbele submit
    if (loading) {
      console.log('⚠️ Google login already in progress, ignoring...');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      console.log('🔐 Starting Google login...');
      const authUser = await loginWithGoogle();
      console.log('✅ Google login completed, tracking...');
      trackLogin('google');
      await handleAuthSuccess(authUser);
    } catch (error: any) {
      console.error('Google login failed:', error);
      if (error.code !== 'auth/popup-closed-by-user') {
        setError('Google inloggen mislukt. Probeer het opnieuw.');
      }
      setLoading(false);
      setPendingNavigation(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Voorkom dubbele submit
    if (loading) {
      console.log('⚠️ Login already in progress, ignoring...');
      return;
    }
    
    if (!signInEmail || !signInPassword) {
      setError('Vul alle velden in');
      return;
    }

    try {
      setError('');
      setLoading(true);
      console.log('🔐 Starting email login...');
      const authUser = await login(signInEmail, signInPassword);
      console.log('✅ Login completed, tracking...');
      trackLogin('email');
      await handleAuthSuccess(authUser);
    } catch (error: any) {
      console.error('Sign in failed:', error);
      
      if (error.code === 'auth/user-not-found') {
        setError('Gebruiker niet gevonden');
      } else if (error.code === 'auth/wrong-password') {
        setError('Ongeldig wachtwoord');
      } else if (error.code === 'auth/invalid-email') {
        setError('Ongeldig e-mailadres');
      } else if (error.code === 'auth/invalid-credential') {
        setError('Ongeldige inloggegevens');
      } else {
        setError('Inloggen mislukt. Probeer het opnieuw.');
      }
      setLoading(false);
      setPendingNavigation(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Voorkom dubbele submit
    if (loading) {
      console.log('⚠️ Registration already in progress, ignoring...');
      return;
    }
    
    if (!signUpEmail || !signUpPassword || !signUpConfirmPassword) {
      setError('Vul alle velden in');
      return;
    }

    if (signUpPassword !== signUpConfirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    if (signUpPassword.length < 6) {
      setError('Wachtwoord moet minimaal 6 karakters bevatten');
      return;
    }

    try {
      setError('');
      setLoading(true);
      const authUser = await register(signUpEmail, signUpPassword);
      trackSignUp('email');
      await handleAuthSuccess(authUser);
    } catch (error: any) {
      console.error('Sign up failed:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        setError('Dit e-mailadres is al in gebruik');
      } else if (error.code === 'auth/invalid-email') {
        setError('Ongeldig e-mailadres');
      } else if (error.code === 'auth/weak-password') {
        setError('Wachtwoord is te zwak');
      } else {
        setError('Registratie mislukt. Probeer het opnieuw.');
      }
      setLoading(false);
      setPendingNavigation(false);
    }
  };

  // ============================================
  // FORGOT PASSWORD HANDLER
  // ============================================
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail) {
      setError('Vul je e-mailadres in');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setError('Vul een geldig e-mailadres in');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, resetEmail, {
        // Optional: customize the reset email
        url: window.location.origin, // Redirect URL after password reset
      });
      
      console.log('📧 Password reset email sent to:', resetEmail);
      setResetEmailSent(true);
      setLoading(false);
      
    } catch (error: any) {
      console.error('Password reset failed:', error);
      
      if (error.code === 'auth/user-not-found') {
        // For security, we show success even if user doesn't exist
        // This prevents email enumeration attacks
        setResetEmailSent(true);
      } else if (error.code === 'auth/invalid-email') {
        setError('Ongeldig e-mailadres');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Te veel verzoeken. Probeer het later opnieuw.');
      } else {
        setError('Er ging iets mis. Probeer het later opnieuw.');
      }
      setLoading(false);
    }
  };

  // Navigate to forgot password view
  const showForgotPassword = () => {
    setError('');
    setResetEmail(signInEmail); // Pre-fill with sign in email if available
    setCurrentView('forgot-password');
  };

  // Navigate back to auth view
  const backToAuth = () => {
    setError('');
    setResetEmailSent(false);
    setCurrentView('auth');
  };

  // Get price display for pending purchase
  const getPriceDisplay = (productId: string): string => {
    const prices: Record<string, string> = {
      'exam_single': '€4,99',
      'pro_monthly': '€9,99/maand',
      'pro_yearly': '€79,99/jaar',
      'ai_pack_50': '€2,99',
      'ai_pack_150': '€6,99',
    };
    return prices[productId] || '';
  };

  // ============================================
  // RENDER: FORGOT PASSWORD VIEW
  // ============================================
  const renderForgotPasswordView = () => (
    <div className="space-y-4">
      {/* Back Button */}
      <button
        onClick={backToAuth}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition text-sm font-medium"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Terug naar inloggen
      </button>

      {resetEmailSent ? (
        // Success State
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MailIcon className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Check je inbox
          </h3>
          <p className="text-gray-500 text-sm mb-6">
            We hebben een e-mail gestuurd naar <strong className="text-gray-700">{resetEmail}</strong> met 
            instructies om je wachtwoord te resetten.
          </p>
          <div className="space-y-3">
            <p className="text-xs text-gray-400">
              Geen e-mail ontvangen? Check je spam folder of probeer het opnieuw.
            </p>
            <button
              onClick={() => {
                setResetEmailSent(false);
                setError('');
              }}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Opnieuw versturen
            </button>
          </div>
        </div>
      ) : (
        // Reset Form
        <>
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Wachtwoord vergeten?
            </h3>
            <p className="text-gray-500 text-sm">
              Geen probleem! Vul je e-mailadres in en we sturen je een link om je wachtwoord te resetten.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                E-mailadres
              </label>
              <input
                type="email"
                id="reset-email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="je@email.com"
                autoFocus
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Versturen...
                </span>
              ) : (
                'Verstuur reset link'
              )}
            </button>
          </form>
        </>
      )}
    </div>
  );

  // ============================================
  // RENDER: AUTH VIEW (Sign In / Sign Up)
  // ============================================
  const renderAuthView = () => (
    <>
      {/* Header */}
      <Dialog.Title as="h3" className="text-2xl font-bold text-center text-gray-900 mb-2">
        {pendingPurchase ? 'Log in om te kopen' : 'Welkom bij WFT Study Buddy'}
      </Dialog.Title>
      
      <p className="text-center text-gray-500 text-sm mb-6">
        {pendingPurchase 
          ? 'Maak een account of log in om door te gaan' 
          : 'Log in of maak een account om te beginnen'
        }
      </p>

      {/* Pending Purchase Display */}
      {pendingPurchase && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">{pendingPurchase.productName}</p>
                <p className="text-xs text-gray-500">Je geselecteerde product</p>
              </div>
            </div>
            <span className="font-bold text-blue-600">{getPriceDisplay(pendingPurchase.productId)}</span>
          </div>
        </div>
      )}

      {/* Loading overlay when waiting for navigation */}
      {pendingNavigation && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl z-10">
          <div className="text-center">
            <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 text-sm">Even geduld...</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition
              ${selected
                ? 'bg-white text-blue-700 shadow'
                : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
              }`
            }
          >
            Inloggen
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition
              ${selected
                ? 'bg-white text-blue-700 shadow'
                : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
              }`
            }
          >
            Registreren
          </Tab>
        </Tab.List>

        <Tab.Panels>
          {/* Sign In Panel */}
          <Tab.Panel>
            <div className="space-y-4">
              {/* Google Sign In Button */}
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Doorgaan met Google
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500">of met e-mail</span>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Sign In Form */}
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label htmlFor="signin-email" className="block text-sm font-medium text-gray-700 mb-1">
                    E-mailadres
                  </label>
                  <input
                    type="email"
                    id="signin-email"
                    value={signInEmail}
                    onChange={(e) => setSignInEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="je@email.com"
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="signin-password" className="block text-sm font-medium text-gray-700">
                      Wachtwoord
                    </label>
                    <button
                      type="button"
                      onClick={showForgotPassword}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Wachtwoord vergeten?
                    </button>
                  </div>
                  <input
                    type="password"
                    id="signin-password"
                    value={signInPassword}
                    onChange={(e) => setSignInPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="••••••••"
                    disabled={loading}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Bezig...
                    </span>
                  ) : (
                    pendingPurchase ? 'Inloggen en doorgaan naar betaling' : 'Inloggen'
                  )}
                </button>
              </form>
            </div>
          </Tab.Panel>

          {/* Sign Up Panel */}
          <Tab.Panel>
            <div className="space-y-4">
              {/* Google Sign Up Button */}
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Registreren met Google
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500">of met e-mail</span>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Sign Up Form */}
              <form onSubmit={handleSignUp} className="space-y-4">
                <div>
                  <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1">
                    E-mailadres
                  </label>
                  <input
                    type="email"
                    id="signup-email"
                    value={signUpEmail}
                    onChange={(e) => setSignUpEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="je@email.com"
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Wachtwoord
                  </label>
                  <input
                    type="password"
                    id="signup-password"
                    value={signUpPassword}
                    onChange={(e) => setSignUpPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Minimaal 6 karakters"
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                    Bevestig wachtwoord
                  </label>
                  <input
                    type="password"
                    id="signup-confirm"
                    value={signUpConfirmPassword}
                    onChange={(e) => setSignUpConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="••••••••"
                    disabled={loading}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Bezig...
                    </span>
                  ) : (
                    pendingPurchase ? 'Account maken en doorgaan' : 'Account aanmaken'
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Door te registreren ga je akkoord met onze{' '}
                  <a href="/terms" className="text-blue-600 hover:underline">Algemene Voorwaarden</a>
                  {' '}en{' '}
                  <a href="/privacy" className="text-blue-600 hover:underline">Privacybeleid</a>
                </p>
              </form>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </>
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Close Button */}
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
                  disabled={loading}
                >
                  <XIcon className="w-5 h-5" />
                </button>

                {/* Render based on current view */}
                {currentView === 'forgot-password' 
                  ? renderForgotPasswordView() 
                  : renderAuthView()
                }
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AuthModal;